import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { SharedParameter, Station } from '../../types/api';
import { countParameterUsage } from '../../utils/parameterUsage';
import NewParameterDialog, { apiErrorMessage, SCALES } from './NewParameterDialog';

const ParametersBank: React.FC = () => {
  const { lang } = useDirection();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ name: '', description: '', maxGrade: 7 });
  const [error, setError] = useState('');

  const { data: parameters = [], isError } = useQuery<SharedParameter[]>({
    queryKey: ['parameters'],
    queryFn: () => apiService.getParameters(),
  });
  const { data: stations = [], isError: stationsError } = useQuery<Station[]>({
    queryKey: ['bankStations'],
    queryFn: () => apiService.getBankStations(),
  });
  const { data: forms = [], isError: formsError } = useQuery({
    queryKey: ['formTemplates'],
    queryFn: () => apiService.getFormTemplates(),
  });
  const { data: statements = [] } = useQuery({
    queryKey: ['statementBank'],
    queryFn: () => apiService.getStatementBank(),
  });

  const update = useMutation({
    mutationFn: (p: { id: string; data: { name: string; description: string | null; maxGrade: number } }) =>
      apiService.updateParameter(p.id, p.data),
    onSuccess: () => {
      // server-side propagation rewrote bank stations / form templates
      ['parameters', 'bankStations', 'formTemplates'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setEditId(null);
    },
    onError: (e: any) => setError(apiErrorMessage(e, tr('param.saveError', lang))),
  });

  const count = (
    n: number,
    one: 'param.station' | 'param.form' | 'param.statement',
    many: 'param.stations' | 'param.forms' | 'param.statements',
  ) => (
    <>
      <span dir="ltr">{n}</span> {tr(n === 1 ? one : many, lang)}
    </>
  );

  const startEdit = (p: SharedParameter) => {
    setError('');
    setEditId(p.id);
    setDraft({ name: p.name, description: p.description ?? '', maxGrade: p.maxGrade });
  };

  const save = (id: string) => {
    if (!draft.name.trim()) {
      setError(tr('param.nameRequired', lang));
      return;
    }
    setError('');
    update.mutate({
      id,
      data: { name: draft.name.trim(), description: draft.description.trim() || null, maxGrade: draft.maxGrade },
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          {tr('param.add', lang)}
        </Button>
      </Box>
      {isError && <Alert severity="error" sx={{ mb: 2 }}>{tr('param.loadError', lang)}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{tr('param.name', lang)}</TableCell>
              <TableCell>{tr('param.description', lang)}</TableCell>
              <TableCell>{tr('param.scale', lang)}</TableCell>
              <TableCell>{tr('param.usedIn', lang)}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {parameters.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">{tr('param.empty', lang)}</Typography>
                </TableCell>
              </TableRow>
            )}
            {parameters.map((p) => {
              const u = countParameterUsage(p.id, stations, forms, statements);
              const editing = editId === p.id;
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    {editing ? (
                      <TextField
                        size="small"
                        value={draft.name}
                        inputProps={{ 'aria-label': tr('param.name', lang) }}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      />
                    ) : (
                      p.name
                    )}
                  </TableCell>
                  <TableCell>
                    {editing ? (
                      <TextField
                        size="small"
                        value={draft.description}
                        inputProps={{ 'aria-label': tr('param.description', lang) }}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      />
                    ) : (
                      p.description
                    )}
                  </TableCell>
                  <TableCell>
                    {editing ? (
                      <Select
                        size="small"
                        value={draft.maxGrade}
                        inputProps={{ 'aria-label': tr('param.scale', lang) }}
                        onChange={(e) => setDraft({ ...draft, maxGrade: Number(e.target.value) })}
                      >
                        {SCALES.map((n) => (
                          <MenuItem key={n} value={n}>{`1–${n}`}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <span dir="ltr">{`1–${p.maxGrade}`}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {stationsError || formsError ? (
                      '—'
                    ) : (
                      <>
                        {count(u.stations, 'param.station', 'param.stations')} ·{' '}
                        {count(u.forms, 'param.form', 'param.forms')} ·{' '}
                        {count(u.statements, 'param.statement', 'param.statements')}
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editing ? (
                      <>
                        <Button size="small" onClick={() => save(p.id)} disabled={update.isPending}>
                          {tr('param.save', lang)}
                        </Button>
                        <Button size="small" onClick={() => setEditId(null)}>
                          {tr('param.cancel', lang)}
                        </Button>
                      </>
                    ) : (
                      <IconButton size="small" aria-label={tr('param.edit', lang)} onClick={() => startEdit(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      <NewParameterDialog
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(p) => {
          setAdding(false);
          qc.setQueryData<SharedParameter[]>(['parameters'], (old) => {
            if (!old) return [p];
            if (old.some((x) => x.id === p.id)) return old;
            return [...old, p];
          });
          qc.invalidateQueries({ queryKey: ['parameters'] });
        }}
      />
    </Box>
  );
};

export default ParametersBank;
